import React, { useState, useEffect, useCallback } from 'react';
import { useMsal } from '@azure/msal-react';
import { authenticatedApiCall } from '../../utils/apiClient';

function AddressLines({ division }) {
  const lines = [division.address_line1, division.address_line2].filter(Boolean);
  if (!lines.length) return '—';
  return lines.map((line, i) => <div key={i}>{line}</div>);
}

const DivisionManagement = () => {
  const { instance, accounts } = useMsal();
  const [divisions, setDivisions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [showAddForm, setShowAddForm] = useState(false);
  const [newDivision, setNewDivision] = useState({ division_number: '', division_name: '', region: '', location: '' });
  const [saving, setSaving] = useState(false);

  const fetchDivisions = useCallback(async () => {
    try {
      setLoading(true);
      const response = await authenticatedApiCall(instance, accounts, 'GET', '/divisions');
      if (response.data.success) {
        setDivisions(response.data.data);
      }
    } catch (err) {
      setError('Failed to load divisions');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [instance, accounts]);

  useEffect(() => {
    fetchDivisions();
  }, [fetchDivisions]);

  const handleAdd = async () => {
    if (!newDivision.division_number || !newDivision.division_name) {
      alert('Division number and name are required');
      return;
    }
    try {
      setSaving(true);
      const response = await authenticatedApiCall(instance, accounts, 'POST', '/divisions', newDivision);
      if (response.data.success) {
        setDivisions([...divisions, response.data.data]);
        setNewDivision({ division_number: '', division_name: '', region: '', location: '' });
        setShowAddForm(false);
      }
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to add division');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (division) => {
    setEditingId(division.id);
    setEditForm({ ...division });
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const response = await authenticatedApiCall(instance, accounts, 'PUT', `/divisions/${editingId}`, editForm);
      if (response.data.success) {
        setDivisions(divisions.map(d => d.id === editingId ? response.data.data : d));
        setEditingId(null);
      }
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to update division');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this division?')) return;
    try {
      const response = await authenticatedApiCall(instance, accounts, 'DELETE', `/divisions/${id}`);
      if (response.data.success) {
        setDivisions(divisions.filter(d => d.id !== id));
      }
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to delete division');
    }
  };

  if (loading) return <div style={{ padding: '2rem', textAlign: 'center' }}>Loading divisions...</div>;
  if (error) return <div style={{ padding: '2rem', color: 'red' }}>{error}</div>;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
        <h3 style={{ margin: 0 }}>Division Management</h3>
        <button
          onClick={() => setShowAddForm(!showAddForm)}
          style={{
            padding: '0.5rem 1rem',
            backgroundColor: '#28a745',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer'
          }}
        >
          {showAddForm ? 'Cancel' : '+ Add Division'}
        </button>
      </div>

      {showAddForm && (
        <div style={{ 
          padding: '1rem', 
          backgroundColor: '#f8f9fa', 
          borderRadius: '8px', 
          marginBottom: '1rem',
          display: 'grid',
          gridTemplateColumns: '0.7fr 1fr 1fr 1fr auto',
          gap: '1rem',
          alignItems: 'end'
        }}>
          <div>
            <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.85rem', color: '#666' }}>Division #</label>
            <input
              type="text"
              value={newDivision.division_number}
              onChange={(e) => setNewDivision({ ...newDivision, division_number: e.target.value })}
              style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px' }}
              placeholder="e.g., DIV001"
            />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.85rem', color: '#666' }}>Division Name</label>
            <input
              type="text"
              value={newDivision.division_name}
              onChange={(e) => setNewDivision({ ...newDivision, division_name: e.target.value })}
              style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px' }}
              placeholder="e.g., Engineering"
            />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.85rem', color: '#666' }}>Region</label>
            <input
              type="text"
              value={newDivision.region}
              onChange={(e) => setNewDivision({ ...newDivision, region: e.target.value })}
              style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px' }}
              placeholder="e.g., USA North"
            />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.85rem', color: '#666' }}>Location</label>
            <input
              type="text"
              value={newDivision.location}
              onChange={(e) => setNewDivision({ ...newDivision, location: e.target.value })}
              style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px' }}
              placeholder="e.g., New York"
            />
          </div>
          <button
            onClick={handleAdd}
            disabled={saving}
            style={{
              padding: '0.5rem 1.5rem',
              backgroundColor: '#007bff',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: saving ? 'not-allowed' : 'pointer',
              opacity: saving ? 0.7 : 1
            }}
          >
            {saving ? 'Saving...' : 'Add'}
          </button>
        </div>
      )}

      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ backgroundColor: '#f8f9fa' }}>
            <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>Division #</th>
            <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>Name</th>
            <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>Region</th>
            <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>Location</th>
            <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '2px solid #dee2e6' }} title="From Settings → Letterhead">Address</th>
            <th style={{ padding: '0.75rem', textAlign: 'center', borderBottom: '2px solid #dee2e6', width: '150px' }}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {divisions.length === 0 ? (
            <tr>
              <td colSpan="6" style={{ padding: '2rem', textAlign: 'center', color: '#666' }}>
                No divisions found. Add one to get started.
              </td>
            </tr>
          ) : (
            divisions.map(division => (
              <tr key={division.id} style={{ borderBottom: '1px solid #dee2e6' }}>
                {editingId === division.id ? (
                  <>
                    <td style={{ padding: '0.5rem' }}>
                      <input
                        type="text"
                        value={editForm.division_number}
                        onChange={(e) => setEditForm({ ...editForm, division_number: e.target.value })}
                        style={{ width: '100%', padding: '0.4rem', border: '1px solid #ddd', borderRadius: '4px' }}
                      />
                    </td>
                    <td style={{ padding: '0.5rem' }}>
                      <input
                        type="text"
                        value={editForm.division_name}
                        onChange={(e) => setEditForm({ ...editForm, division_name: e.target.value })}
                        style={{ width: '100%', padding: '0.4rem', border: '1px solid #ddd', borderRadius: '4px' }}
                      />
                    </td>
                    <td style={{ padding: '0.5rem' }}>
                      <input
                        type="text"
                        value={editForm.region || ''}
                        onChange={(e) => setEditForm({ ...editForm, region: e.target.value })}
                        style={{ width: '100%', padding: '0.4rem', border: '1px solid #ddd', borderRadius: '4px' }}
                      />
                    </td>
                    <td style={{ padding: '0.5rem' }}>
                      <input
                        type="text"
                        value={editForm.location || ''}
                        onChange={(e) => setEditForm({ ...editForm, location: e.target.value })}
                        style={{ width: '100%', padding: '0.4rem', border: '1px solid #ddd', borderRadius: '4px' }}
                      />
                    </td>
                    <td style={{ padding: '0.5rem', fontSize: '0.85rem', lineHeight: 1.35, color: '#555' }}>
                      <AddressLines division={editForm} />
                    </td>
                    <td style={{ padding: '0.5rem', textAlign: 'center' }}>
                      <button
                        onClick={handleSave}
                        disabled={saving}
                        style={{ padding: '0.3rem 0.75rem', backgroundColor: '#28a745', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', marginRight: '0.5rem' }}
                      >
                        Save
                      </button>
                      <button
                        onClick={() => setEditingId(null)}
                        style={{ padding: '0.3rem 0.75rem', backgroundColor: '#6c757d', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                      >
                        Cancel
                      </button>
                    </td>
                  </>
                ) : (
                  <>
                    <td style={{ padding: '0.75rem' }}>{division.division_number}</td>
                    <td style={{ padding: '0.75rem' }}>{division.division_name}</td>
                    <td style={{ padding: '0.75rem' }}>{division.region || '—'}</td>
                    <td style={{ padding: '0.75rem' }}>{division.location || '—'}</td>
                    <td style={{ padding: '0.75rem', fontSize: '0.85rem', lineHeight: 1.35 }}>
                      <AddressLines division={division} />
                    </td>
                    <td style={{ padding: '0.75rem', textAlign: 'center' }}>
                      <button
                        onClick={() => handleEdit(division)}
                        style={{ padding: '0.3rem 0.75rem', backgroundColor: '#007bff', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', marginRight: '0.5rem' }}
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(division.id)}
                        style={{ padding: '0.3rem 0.75rem', backgroundColor: '#dc3545', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                      >
                        Delete
                      </button>
                    </td>
                  </>
                )}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
};

export default DivisionManagement;










